// components/extras/ExtraAddItemModal.js
// Modal per aggiungere voci all'extra (da listino o manualmente)

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Search, Plus } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { parseItalianNumber, normalizeNumericInput } from '../../lib/numericUtils';

const ExtraAddItemModal = ({
  open,
  onOpenChange,
  currentCategory,
  subcategories,
  selectedSubcategory,
  setSelectedSubcategory,
  searchTerm,
  setSearchTerm,
  newItem,
  setNewItem,
  filteredPriceList,
  onAddFromPriceList,
  onAddManualItem
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Aggiungi Voce - {currentCategory?.label}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Subcategory Filter */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Sottocategoria</Label>
              <Select
                value={selectedSubcategory}
                onValueChange={setSelectedSubcategory}
              >
                <SelectTrigger data-testid="subcategory-filter">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sottocategorie</SelectItem>
                  {subcategories.map(sub => (
                    <SelectItem key={sub.code} value={sub.code}>{sub.code} - {sub.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Cerca</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#737373]" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Cerca per codice o descrizione..."
                  className="pl-10"
                  data-testid="price-list-search"
                />
              </div>
            </div>
            <div className="w-24">
              <Label>Quantità</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={newItem.quantity}
                onChange={(e) => setNewItem(prev => ({ ...prev, quantity: normalizeNumericInput(e.target.value) }))}
                onBlur={(e) => {
                  const parsed = parseItalianNumber(e.target.value, 1);
                  setNewItem(prev => ({ ...prev, quantity: Math.max(0.01, parsed) }));
                }}
                data-testid="new-item-quantity"
              />
            </div>
          </div>

          {/* Price List */}
          <ScrollArea className="h-[300px] border rounded-lg">
            <div className="p-2 space-y-1">
              {filteredPriceList.length === 0 ? (
                <div className="text-center py-8 text-[#737373]" data-testid="empty-price-list">
                  Nessuna voce trovata
                </div>
              ) : (
                filteredPriceList.map(item => (
                  <PriceListItem
                    key={item.id}
                    item={item}
                    onAdd={onAddFromPriceList}
                  />
                ))
              )}
            </div>
          </ScrollArea>

          <Separator />

          {/* Manual Entry */}
          <ManualEntryForm
            newItem={newItem}
            setNewItem={setNewItem}
            onAddManualItem={onAddManualItem}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Price list item component
const PriceListItem = ({ item, onAdd }) => (
  <div
    className="flex items-center justify-between p-3 hover:bg-[#FAFAFA] rounded-lg cursor-pointer border border-transparent hover:border-[#DCDCDC]"
    onClick={() => onAdd(item)}
    data-testid={`price-list-item-${item.id}`}
  >
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-[#FF7819] bg-[#FFF0E5] px-2 py-0.5 rounded">
          {item.code}
        </span>
        {item.subcategory && (
          <span className="text-xs text-[#737373]">{item.subcategory}</span>
        )}
      </div>
      <p className="text-sm mt-1">{item.description}</p>
    </div>
    <div className="text-right ml-4">
      <p className="font-medium">{formatCurrency(item.unit_price)}</p>
      <p className="text-xs text-[#737373]">/{item.unit}</p>
    </div>
  </div>
);

// Manual entry form component
const ManualEntryForm = ({ newItem, setNewItem, onAddManualItem }) => (
  <div>
    <h4 className="font-medium mb-3">Oppure inserisci manualmente</h4>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div>
        <Label>Codice</Label>
        <Input
          value={newItem.code}
          onChange={(e) => setNewItem(prev => ({ ...prev, code: e.target.value }))}
          placeholder="ES001"
          data-testid="manual-code-input"
        />
      </div>
      <div className="col-span-2">
        <Label>Descrizione</Label>
        <Input
          value={newItem.description}
          onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Descrizione voce..."
          data-testid="manual-description-input"
        />
      </div>
      <div>
        <Label>U.M.</Label>
        <Input
          value={newItem.unit}
          onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
          placeholder="nr"
          data-testid="manual-unit-input"
        />
      </div>
      <div>
        <Label>Quantità</Label>
        <Input
          type="text"
          inputMode="decimal"
          value={newItem.quantity}
          onChange={(e) => setNewItem(prev => ({ ...prev, quantity: normalizeNumericInput(e.target.value) }))}
          onBlur={(e) => {
            const parsed = parseItalianNumber(e.target.value, 1);
            setNewItem(prev => ({ ...prev, quantity: Math.max(0.01, parsed) }));
          }}
          data-testid="manual-quantity-input"
        />
      </div>
      <div>
        <Label>Prezzo Unitario</Label>
        <Input
          type="text"
          inputMode="decimal"
          value={newItem.unit_price}
          onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: normalizeNumericInput(e.target.value) }))}
          onBlur={(e) => {
            const parsed = parseItalianNumber(e.target.value, 0);
            setNewItem(prev => ({ ...prev, unit_price: Math.max(0, parsed) }));
          }}
          data-testid="manual-price-input"
        />
      </div>
      <div>
        <Label>IVA %</Label>
        <Select
          value={String(newItem.vat_percentage)}
          onValueChange={(v) => setNewItem(prev => ({ ...prev, vat_percentage: parseInt(v) }))}
        >
          <SelectTrigger data-testid="manual-vat-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="4">4%</SelectItem>
            <SelectItem value="10">10%</SelectItem>
            <SelectItem value="22">22%</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>&nbsp;</Label>
        <Button
          className="w-full btn-primary"
          onClick={onAddManualItem}
          disabled={!newItem.code || !newItem.description}
          data-testid="add-manual-item-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Aggiungi
        </Button>
      </div>
    </div>
  </div>
);

export default ExtraAddItemModal;
